import React, { useState } from 'react';
import { themeColor } from '../utils/themeColor';
import './Dashboard.css';
import MetricCard from './MetricCard';
import RiskIndicator from './RiskIndicator';
import CalculationModal from './CalculationModal';
import PayoutPlanner from './PayoutPlanner';
import { IconTrendDown, IconTrendUp, IconAlert, IconChart, IconScale, IconInfo, IconCheck, IconLightbulb } from './Icons';
import { getResultsNavLabel } from '../utils/activeEntity';

function Dashboard({ metrics, riskMode = 'risk', onNavigate, formData, consistencyPercent = 50 }) {
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showRiskBasisModal, setShowRiskBasisModal] = useState(false);
  const [showMultiTpModal, setShowMultiTpModal] = useState(false);
  const analyzedStrategyName = formData?.sheetName || null;
  const canOpenVectorResults =
    Boolean(onNavigate) &&
    Boolean(analyzedStrategyName) &&
    analyzedStrategyName !== 'Sample Strategy (Demo)';

  const handleOpenVectorResults = () => {
    if (!canOpenVectorResults) return;
    localStorage.setItem('results.selectedSheet', analyzedStrategyName);
    onNavigate('results');
  };
  if (!metrics || metrics.error) {
    return (
      <div className="dashboard-error">
        {metrics?.error || 'Unable to display metrics'}
      </div>
    );
  }
  const activeConsistencyPercent = Number(metrics.consistencyPercent || metrics.windfallRule?.consistencyPercent || consistencyPercent || 50);
  const isNoStrategyAssigned = metrics?.riskMessage === 'No strategy assigned';

  if (isNoStrategyAssigned) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title">
              {riskMode === 'apexMae' ? `${activeConsistencyPercent}% Rule Results` : 'Blowout Risk Results'}
            </h2>
          </div>
        </div>
        <div className="dashboard-error">
          No strategy assigned for this account row. Select a strategy in Bulk Risk to run full analysis.
        </div>
      </div>
    );
  }

  // Debug logging (can be removed later)
  console.log('Dashboard metrics:', {
    maxProfit: metrics.maxProfit,
    avgProfit: metrics.avgProfit,
    maxProfitPercent: metrics.maxProfitPercent,
    avgProfitPercent: metrics.avgProfitPercent,
    windfallRule: metrics.windfallRule,
    riskMode: riskMode,
    allKeys: Object.keys(metrics),
    windfallLargestSafeDay: metrics.windfallRule?.largestSafeDayForFastestPayout,
    windfallApexMin: metrics.windfallRule?.apexMinPayoutProfit
  });

  return (
    <div className="dashboard">
      {/* Blowout Risk Results Header - appears at top for Risk mode */}
      {riskMode === 'risk' && (
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title">Blowout Risk Results</h2>
          </div>
        </div>
      )}

      {/* GO/NO GO Indicator - only show for Risk mode */}
      {riskMode === 'risk' && metrics.blowAccountStatus && (
        <div className="blow-account-indicator" style={{ borderColor: metrics.blowAccountColor, backgroundColor: `${metrics.blowAccountColor}15` }}>
          <div className="blow-account-status">
            <span className="blow-account-label">Account Blowout Risk:</span>
            <span className="blow-account-value" style={{ color: metrics.blowAccountColor }}>
              {metrics.blowAccountStatus}
            </span>
          </div>
          {metrics.contractType && (
            <div className="contract-type-badge">
              Contract Type: <strong>{metrics.contractType}</strong>
            </div>
          )}
          <p className="blow-account-message">{metrics.blowAccountMessage}</p>
          {metrics.blowAccountProbability !== null && (
            <div className="blow-account-probability">
              Probability of end-of-day losses exceeding max drawdown: <strong>{metrics.blowAccountProbability.toFixed(1)}%</strong>
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.3)', fontSize: '0.9em' }}>
                <strong><IconAlert size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> Important:</strong> Trailing drawdown is based on <strong>intraday drawdown movement</strong>, not end-of-day P&amp;L. A trade could close positive but still blow the account if it exceeded the drawdown limit during the day. This analysis uses end-of-day data, so <strong>actual risk may be higher</strong> than shown.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Risk Score Display - appears after Account Blowout Risk, with Risk Indicator on same line */}
      {riskMode === 'risk' && (
        <div className="dashboard-header risk-score-header" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <div className="risk-score-display">
            <span className="risk-score-label">Risk Score:</span>
            <span className="risk-score-value" style={{ color: metrics.riskColor }}>
              {metrics.riskScore}/100
            </span>
          </div>
          <RiskIndicator 
            level={metrics.riskLevel} 
            color={metrics.riskColor}
            message={metrics.riskMessage}
          />
        </div>
      )}

      {/* % Rule Results Header */}
      {riskMode === 'apexMae' && (
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title">{activeConsistencyPercent}% Rule Results</h2>
          </div>
        </div>
      )}

      {(metrics.riskCalculationNote || (metrics.hasMultipleTpTargets && metrics.multiTpModelingNote) || canOpenVectorResults) && (
        <div className="dashboard-action-row">
          {metrics.riskCalculationNote && (
            <button
              type="button"
              className="dashboard-inline-btn dashboard-inline-btn-neutral"
              onClick={() => setShowRiskBasisModal(true)}
            >
              <IconInfo size={14} />
              <span>How RiskLo Chooses Risk Inputs</span>
            </button>
          )}
          {metrics.hasMultipleTpTargets && metrics.multiTpModelingNote && (
            <button
              type="button"
              className="dashboard-inline-btn dashboard-inline-btn-warning"
              onClick={() => setShowMultiTpModal(true)}
            >
              <IconInfo size={14} />
              <span>Multi-TP note</span>
            </button>
          )}
          {canOpenVectorResults && (
            <button type="button" className="dashboard-inline-btn dashboard-inline-btn-indigo" onClick={handleOpenVectorResults}>
              View this strategy in {getResultsNavLabel()}
            </button>
          )}
        </div>
      )}

      {riskMode === 'risk' && (
        // Full metrics for Risk mode
        <div className="metrics-grid">
          <MetricCard
            title="Risk Score"
            value={`${metrics.riskScore}/100`}
            subtitle={metrics.riskScore < 40 ? 'Low Risk' : metrics.riskScore < 70 ? 'Moderate Risk' : 'High Risk'}
            icon={<IconAlert size={20} />}
            trend={metrics.riskScore < 40 ? 'neutral' : metrics.riskScore < 70 ? 'moderate' : 'negative'}
            clickable={true}
            onClick={() => setSelectedMetric('riskScore')}
          />
          
          <MetricCard
            title="Highest Loss"
            value={
              <span className="metric-value-loss">
                ${metrics.highestLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            }
            subtitle={
              <>
                <span className="metric-subtitle-primary">
                  {metrics.highestLossPercent}% of account ({metrics.numContracts || 1} contract{(metrics.numContracts || 1) > 1 ? 's' : ''})
                </span>
                {metrics.highestLossDate && (
                  <span className="metric-subtitle-date metric-subtitle-date-loss">Worst: {metrics.highestLossDate}</span>
                )}
              </>
            }
            icon={<IconTrendDown size={20} />}
            trend="negative"
            clickable={true}
            onClick={() => setSelectedMetric('highestLoss')}
          />
          
          <MetricCard
            title="Average Loss"
            value={
              <span className="metric-value-loss">
                ${metrics.avgLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            }
            subtitle={`${metrics.avgLossPercent}% of account (${metrics.numContracts || 1} contract${(metrics.numContracts || 1) > 1 ? 's' : ''})`}
            icon={<IconChart size={20} />}
            trend="negative"
            clickable={true}
            onClick={() => setSelectedMetric('averageLoss')}
          />

          {metrics.theoreticalStopLoss !== null && metrics.theoreticalStopLoss !== undefined && (
            <MetricCard
              title="SL-Based Theoretical Loss"
              value={
                <span className="metric-value-loss">
                  ${metrics.theoreticalStopLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`${metrics.theoreticalStopLossPercent || '0.00'}% of account (${metrics.strategySlLimitPoints || 0} pts SL)`}
              icon={<IconScale size={20} />}
              trend="moderate"
              clickable={true}
              onClick={() => setSelectedMetric('slLoss')}
            />
          )}
          
          {metrics.maxProfit !== undefined && metrics.maxProfit > 0 && (
            <MetricCard
              title="Max Profit"
              value={
                <span className="metric-value-profit">
                  ${metrics.maxProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={
                <>
                  <span className="metric-subtitle-primary">
                    {metrics.maxProfitPercent}% of account ({metrics.numContracts || 1} contract{(metrics.numContracts || 1) > 1 ? 's' : ''})
                  </span>
                  {metrics.maxProfitDate && (
                    <span className="metric-subtitle-date metric-subtitle-date-profit">Best: {metrics.maxProfitDate}</span>
                  )}
                </>
              }
              icon={<IconTrendUp size={20} />}
              trend="positive"
              clickable={true}
              onClick={() => setSelectedMetric('maxProfit')}
            />
          )}
          
          {metrics.avgProfit !== undefined && metrics.avgProfit > 0 && (
            <MetricCard
              title="Average Profit"
              value={
                <span className="metric-value-profit">
                  ${metrics.avgProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`${metrics.avgProfitPercent}% of account (${metrics.numContracts || 1} contract${(metrics.numContracts || 1) > 1 ? 's' : ''})`}
              icon={<IconTrendUp size={20} />}
              trend="positive"
              clickable={true}
              onClick={() => setSelectedMetric('averageProfit')}
            />
          )}
          
          {metrics.highestLossPerContract && (
            <MetricCard
              title="Per-Contract Loss"
              value={
                <span className="metric-value-loss">
                  ${metrics.highestLossPerContract.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`Worst loss per contract`}
              icon={<IconScale size={20} />}
              trend="neutral"
            />
          )}
          
          <MetricCard
            title="Trading Days Analyzed"
            value={metrics.totalDays}
            subtitle={metrics.winningDays !== undefined 
              ? `${metrics.winningDays} winning, ${metrics.losingDays} losing days`
              : `${metrics.losingDays} losing days`}
            icon="📈"
            trend="neutral"
          />

          {metrics.drawdownBreach && (
            <MetricCard
              title="Max Drawdown Analysis"
              value={`$${metrics.drawdownBreach.maxDrawdown.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={metrics.drawdownBreach.breaches > 0 
                ? `${metrics.drawdownBreach.breaches} breach(es) with ${metrics.numContracts || 1} contract(s) - ${metrics.drawdownBreach.breachProbability}% probability`
                : `No breaches - $${parseFloat(metrics.drawdownBreach.margin).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} buffer`}
              icon="🛡️"
              trend={metrics.drawdownBreach.highestExceeds ? 'negative' : 'neutral'}
              clickable={true}
              onClick={() => setSelectedMetric('maxDrawdown')}
            />
          )}
        </div>
      )}

      {/* What to Fix Section - Show when there's a risk issue (Risk mode only - appears after metric cards) */}
      {riskMode === 'risk' && (metrics.blowAccountStatus === 'NO GO' || metrics.blowAccountStatus === 'CAUTION' || metrics.riskScore >= 60) && (
        <div className="what-to-fix-section">
          <div className="what-to-fix-header">
            <IconLightbulb size={20} style={{ color: '#f59e0b' }} />
            <h3 className="what-to-fix-title">What to Fix</h3>
          </div>
          <div className="what-to-fix-content">
            <ul className="what-to-fix-list">
              {/* Suggestion 1: Reduce Contracts */}
              {formData?.contracts && Number(formData.contracts) > 1 && (
                <li className="what-to-fix-item">
                  <strong>Reduce Contracts:</strong> Consider reducing from <strong>{formData.contracts}</strong> contract{Number(formData.contracts) > 1 ? 's' : ''} to{' '}
                  {metrics.drawdownBreach?.maxDrawdown ? (
                    <strong>{Math.max(1, Math.floor((metrics.drawdownBreach.maxDrawdown / (metrics.highestLoss / Number(formData.contracts || 1))) * 0.9))}</strong>
                  ) : (
                    <strong>{Math.max(1, Math.floor(Number(formData.contracts) * 0.5))}</strong>
                  )}
                  {' '}contract{Math.max(1, Math.floor(Number(formData.contracts) * 0.5)) > 1 ? 's' : ''} to lower your risk exposure.
                </li>
              )}
              
              {/* Suggestion 2: Switch to MNQ */}
              {formData?.contractType === 'NQ' && (
                <li className="what-to-fix-item">
                  <strong>Switch to Micro Contracts (MNQ):</strong> MNQ contracts are 1/10th the size of NQ contracts, which means{' '}
                  {formData?.contracts ? `your current ${formData.contracts} NQ contract${Number(formData.contracts) > 1 ? 's' : ''}` : 'your NQ position'}{' '}
                  would be equivalent to <strong>{Number(formData?.contracts || 1) * 10}</strong> MNQ contracts. This gives you{' '}
                  <strong>10x more granular control</strong> over your position size and can help you stay within risk limits.
                </li>
              )}
              
              {/* Suggestion 3: Consider Alternative Strategies (Last Resort) */}
              {formData?.contractType === 'MNQ' && Number(formData?.contracts || 1) === 1 ? (
                <li className="what-to-fix-item">
                  <strong>Consider Alternative Strategies:</strong> Since you're already trading <strong>1 MNQ contract</strong> (the smallest position size), comparing a different strategy may be worth considering.
                  Review other available strategies using the same historical metrics and choose the one that best fits your own risk parameters and independent judgment.
                </li>
              ) : (
                <li className="what-to-fix-item">
                  <strong>Consider Alternative Strategies (Last Resort):</strong> If reducing contracts or switching to MNQ isn't sufficient, this strategy may not align with your risk tolerance or account size.
                  Try analyzing other strategies from the dropdown to find one with lower historical drawdowns, and use your own independent judgment when comparing available options.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {riskMode === 'apexMae' && metrics.apexMaeComparison && (
        <div className="apex-mae-simplified-box" style={{ 
          borderColor: (metrics.apexMaeComparison.exceedsMae || (metrics.windfallRule?.payoutPathStatus && metrics.windfallRule.payoutPathStatus !== 'ALIGNED')) ? '#ef4444' : '#10b981',
          backgroundColor: (metrics.apexMaeComparison.exceedsMae || (metrics.windfallRule?.payoutPathStatus && metrics.windfallRule.payoutPathStatus !== 'ALIGNED')) ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)'
        }}>
          <div className="apex-mae-simplified-header">
            <div className="apex-mae-status-badge" style={{ 
              backgroundColor: (metrics.apexMaeComparison.exceedsMae || (metrics.windfallRule?.payoutPathStatus && metrics.windfallRule.payoutPathStatus !== 'ALIGNED')) ? '#ef4444' : '#10b981',
              color: '#ffffff'
            }}>
              {(metrics.apexMaeComparison.exceedsMae || (metrics.windfallRule?.payoutPathStatus && metrics.windfallRule.payoutPathStatus !== 'ALIGNED')) ? (
                <>
                  <IconAlert size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  REQUIRES ATTENTION
                </>
              ) : (
                <>
                  <IconCheck size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  WITHIN LIMIT
                </>
              )}
            </div>
            <h3 className="apex-mae-simplified-title">{activeConsistencyPercent}% Rule (CONSISTENCY/WINDFALL)</h3>
          </div>
          
          <div className="apex-mae-simplified-content">
            <div className="apex-mae-limits-container" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'stretch' }}>
              {/* Left Column: Consistency (Loss) Information */}
              <div className="apex-mae-left-col" style={{ flex: '1', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Maximum Loss Allowed (Consistency Limit) */}
                <div className="apex-mae-key-metric apex-mae-card-mae-limit" style={{ flex: '0 0 auto' }}>
                  <div className="apex-mae-card-header-stack">
                    <span 
                      className="apex-mae-card-badge"
                      style={{
                        backgroundColor: metrics.apexMaeComparison.exceedsMae ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                        color: metrics.apexMaeComparison.exceedsMae ? '#ef4444' : '#10b981',
                        border: `1px solid ${metrics.apexMaeComparison.exceedsMae ? '#ef4444' : '#10b981'}`
                      }}
                    >
                      {metrics.apexMaeComparison.exceedsMae ? 'REQUIRES ATTENTION' : 'WITHIN LIMIT'}
                    </span>
                    <div className="apex-mae-metric-label">Maximum Loss Allowed (Consistency Limit)</div>
                  </div>
                  <div className="apex-mae-metric-value" style={{ color: metrics.apexMaeComparison.exceedsMae ? '#ef4444' : '#10b981' }}>
                    ${metrics.apexMaeComparison.maxMaePerTrade.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="apex-mae-metric-subtitle">
                    {metrics.apexMaeComparison.limitPercent * 100}% of ${metrics.apexMaeComparison.baseAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>

                {/* Strategy's Worst Historical Loss Day */}
                <div className="apex-mae-card-worst-loss" style={{ 
                  padding: '1.25rem', 
                  background: 'rgba(255, 255, 255, 0.03)', 
                  borderRadius: '8px',
                  border: `1px solid ${metrics.apexMaeComparison.exceedsMae ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)'}`,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '140px',
                  justifyContent: 'space-between'
                }}>
                  <div className="comparison-label" style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                    Strategy's Worst Historical Loss Day
                  </div>
                  <div className={`comparison-value ${metrics.apexMaeComparison.exceedsMae ? 'exceeds' : 'safe'}`} style={{ 
                    fontSize: '2rem', 
                    fontWeight: '700',
                    marginBottom: '0.5rem',
                    lineHeight: '1.2',
                    textAlign: 'center'
                  }}>
                    ${metrics.apexMaeComparison.worstLossForSize.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div style={{ fontSize: '0.8em', opacity: 0.7, fontStyle: 'italic', textAlign: 'center' }}>
                    Based on historical strategy data
                  </div>
                </div>

                {/* Consistency Warning/Status */}
                {metrics.apexMaeComparison.exceedsMae ? (
                  <div className="apex-mae-warning apex-mae-card-mae-status">
                    <strong><IconAlert size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> WARNING (Consistency):</strong> The strategy's worst historical loss day exceeds the consistency limit by ${Math.abs(parseFloat(metrics.apexMaeComparison.maeBuffer)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. 
                    This strategy has historically violated the {activeConsistencyPercent}% rule.
                    {metrics.apexMaeComparison.maeBreachProbability !== undefined && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.15)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        <strong>Probability of exceeding consistency limit:</strong> <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '1.1em' }}>{metrics.apexMaeComparison.maeBreachProbability}%</span>
                        {metrics.apexMaeComparison.maeBreaches > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.9em', opacity: 0.9 }}>
                            ({metrics.apexMaeComparison.maeBreaches} of {metrics.apexMaeComparison.totalTradingDays} trading days exceeded the limit)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="apex-mae-safe apex-mae-card-mae-status">
                    <strong><IconCheck size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> SAFE (Consistency):</strong> The strategy's worst historical loss day is within the consistency limit. 
                    There is a ${metrics.apexMaeComparison.maeBuffer} buffer before hitting the limit.
                    {metrics.apexMaeComparison.maeBreachProbability !== undefined && metrics.apexMaeComparison.maeBreachProbability > 0 && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                        <strong>Probability of exceeding consistency limit:</strong> <span style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '1.1em' }}>{metrics.apexMaeComparison.maeBreachProbability}%</span>
                        {metrics.apexMaeComparison.maeBreaches > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.9em', opacity: 0.9 }}>
                            ({metrics.apexMaeComparison.maeBreaches} of {metrics.apexMaeComparison.totalTradingDays} trading days exceeded the limit)
                          </div>
                        )}
                      </div>
                    )}
                    {metrics.apexMaeComparison.maeBreachProbability !== undefined && metrics.apexMaeComparison.maeBreachProbability === 0 && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                        <strong>Probability of exceeding consistency limit:</strong> <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1.1em' }}>0%</span>
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9em', opacity: 0.9 }}>
                          (No historical trading days exceeded the consistency limit)
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Windfall (Fastest Payout Path) Information */}
              {metrics.windfallRule && (
                <div className="apex-mae-right-col" style={{ flex: '1', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '1.5rem', alignSelf: 'stretch' }}>
                  {/* Largest Safe Strategy Profit Day for Fastest Payout */}
                  <div className="apex-mae-key-metric apex-mae-card-largest-safe" style={{ cursor: 'pointer', flex: '0 0 auto' }} onClick={() => setSelectedMetric('windfall')}>
                    <div className="apex-mae-card-header-stack">
                      <span 
                        className="apex-mae-card-badge"
                        style={{
                          backgroundColor: metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                          color: metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? '#10b981' : '#f59e0b',
                          border: `1px solid ${metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? '#10b981' : '#f59e0b'}`
                        }}
                      >
                        {metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? 'ALIGNED' : 'DELAYED'}
                      </span>
                      <div className="apex-mae-metric-label">Largest Safe Strategy Profit Day for Fastest Payout</div>
                    </div>
                    <div className="apex-mae-metric-value" style={{ color: metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? '#10b981' : '#f59e0b' }}>
                      ${(() => {
                        const value = parseFloat(metrics.windfallRule.largestSafeDayForFastestPayout);
                        return isNaN(value) ? ((activeConsistencyPercent / 100) * 2600).toFixed(2) : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
                    </div>
                    <div className="apex-mae-metric-subtitle">
                      If the strategy's largest profit day stays under this level, a payout at ${(() => {
                        const value = parseFloat(metrics.windfallRule.apexMinPayoutProfit);
                        return isNaN(value) ? '2,600' : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                      })()} is achievable.
                    </div>
                  </div>

                  {/* Strategy's Highest Historical Profit Day */}
                  {metrics.windfallRule?.maxProfitDay !== null && metrics.windfallRule?.maxProfitDay !== undefined && metrics.windfallRule.maxProfitDay > 0 && (
                    <div className="apex-mae-card-highest-profit" style={{ 
                      padding: '1.25rem', 
                      background: 'rgba(255, 255, 255, 0.03)', 
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '140px',
                      justifyContent: 'space-between'
                    }}>
                      <div className="comparison-label" style={{ marginBottom: '0.75rem', fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                        Strategy's Highest Historical Profit Day
                      </div>
                      <div className="comparison-value safe" style={{ 
                        fontSize: '2rem', 
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        lineHeight: '1.2',
                        color: '#10b981',
                        textAlign: 'center'
                      }}>
                        ${parseFloat(metrics.windfallRule.maxProfitDay).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: '0.8em', opacity: 0.7, fontStyle: 'italic', textAlign: 'center' }}>
                        Based on historical strategy data
                      </div>
                    </div>
                  )}


                  {/* Why this matters - Insight Box */}
                  <div className="apex-mae-card-why-matters" style={{ 
                    padding: '1rem', 
                    background: 'rgba(102, 126, 234, 0.1)', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Why this matters</strong>
                      <div style={{ fontSize: '0.9em', opacity: 0.9, lineHeight: '1.5', marginBottom: '0.75rem' }}>
                        Large profit days are allowed under Apex rules. However, the larger a strategy's biggest profit day is, the more total profit is required before a payout can be requested.
                      </div>
                    </div>
                    <div style={{ fontSize: '0.85em', opacity: 0.8, marginTop: 'auto' }}>
                      <a 
                        href="https://support.apextraderfunding.com/hc/en-us/articles/40507212951451-PA-Payout-Parameters" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          color: themeColor('#667eea'), 
                          textDecoration: 'underline',
                          fontWeight: '500'
                        }}
                      >
                        Learn more about Apex windfall rule →
                      </a>
                    </div>
                  </div>

                  {/* Helper text */}
                  <div className="apex-mae-card-helper" style={{ fontSize: '0.85em', opacity: 0.7, fontStyle: 'italic', marginTop: '-0.5rem' }}>
                    Metrics are based on historical strategy performance, not your personal trading results.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Probability Display with Risk Indicator for % Rule */}
      {riskMode === 'apexMae' && (
        <div className="dashboard-apex-probability-row">
          {/* Consistency Probability - Left Side */}
          {metrics.apexMaeComparison?.maeBreachProbability !== undefined && (
            <div className="dashboard-apex-probability-block">
              <div className="dashboard-apex-probability-inner">
                <div className="dashboard-apex-probability-label-row">
                  <span className="risk-score-label">Probability of exceeding consistency limit:</span>
                  <span className="risk-score-value" style={{ 
                    color: metrics.apexMaeComparison.maeBreachProbability >= 60 ? '#ef4444' : 
                           metrics.apexMaeComparison.maeBreachProbability >= 30 ? '#f59e0b' : '#10b981'
                  }}>
                    {metrics.apexMaeComparison.maeBreachProbability}%
                  </span>
                </div>
                <RiskIndicator 
                  level={metrics.apexMaeComparison.maeBreachProbability >= 60 ? 'high' : 
                         metrics.apexMaeComparison.maeBreachProbability >= 30 ? 'moderate' : 'low'}
                  color={metrics.apexMaeComparison.maeBreachProbability >= 60 ? '#ef4444' : 
                         metrics.apexMaeComparison.maeBreachProbability >= 30 ? '#f59e0b' : '#10b981'}
                />
              </div>
            </div>
          )}
          
          {/* Windfall Probability - Right of center (aligned with right edge of left warning box) */}
          {metrics.windfallRule?.probabilityExceedingFastestPayoutThreshold !== undefined && metrics.windfallRule?.probabilityExceedingFastestPayoutThreshold !== null && (
            <div className="dashboard-apex-probability-block dashboard-apex-probability-windfall">
              <div className="dashboard-apex-probability-inner">
                <div className="dashboard-apex-probability-content">
                  <div className="dashboard-apex-probability-label-row">
                    <span className="risk-score-label">Probability of exceeding {`$${((activeConsistencyPercent / 100) * 2600).toFixed(0)}`} threshold:</span>
                    <span className="risk-score-value" style={{ 
                      color: metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 60 ? '#ef4444' : 
                             metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 30 ? '#f59e0b' : '#10b981'
                    }}>
                      {metrics.windfallRule.probabilityExceedingFastestPayoutThreshold}%
                    </span>
                  </div>
                  {metrics.windfallRule.daysExceedingFastestPayoutThreshold !== undefined && metrics.windfallRule.totalTradingDays !== undefined && (
                    <div className="dashboard-apex-probability-detail">
                      ({metrics.windfallRule.daysExceedingFastestPayoutThreshold} of {metrics.windfallRule.totalTradingDays} historical strategy trading days)
                    </div>
                  )}
                  <div className="dashboard-apex-probability-hint">
                    Probability based on historical strategy performance.
                  </div>
                </div>
                <RiskIndicator 
                  level={metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 60 ? 'high' : 
                         metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 30 ? 'moderate' : 'low'}
                  color={metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 60 ? '#ef4444' : 
                         metrics.windfallRule.probabilityExceedingFastestPayoutThreshold >= 30 ? '#f59e0b' : '#10b981'}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metric cards for % Rule mode */}
      {riskMode === 'apexMae' && (
        <div className="metrics-grid" style={{ marginTop: metrics.apexMaeComparison?.maeBreachProbability !== undefined ? '0' : '1.5rem' }}>
          {metrics.apexMaeComparison && (
            <MetricCard
              title="Maximum Loss Allowed"
              value={`$${metrics.apexMaeComparison.maxMaePerTrade.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`Consistency Limit (${(metrics.apexMaeComparison.limitPercent * 100).toFixed(0)}% rule)`}
              icon="🛡️"
              trend={metrics.apexMaeComparison.exceedsMae ? 'negative' : 'neutral'}
              clickable={true}
              onClick={() => setSelectedMetric('apexMae')}
            />
          )}
          
          {metrics.windfallRule && (
            <MetricCard
              title="Largest Safe Strategy Profit Day for Fastest Payout"
              value={`$${(() => {
                const value = parseFloat(metrics.windfallRule.largestSafeDayForFastestPayout);
                return isNaN(value) ? ((activeConsistencyPercent / 100) * 2600).toFixed(2) : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              })()}`}
              subtitle={`Fastest payout path (payout at $${(() => {
                const value = parseFloat(metrics.windfallRule.apexMinPayoutProfit);
                return isNaN(value) ? '2,600' : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              })()} is achievable)`}
              icon={<IconTrendUp size={20} />}
              trend={metrics.windfallRule.payoutPathStatus === 'ALIGNED' ? 'positive' : 'neutral'}
              clickable={true}
              onClick={() => setSelectedMetric('windfall')}
            />
          )}
          
          <MetricCard
            title="Strategy's Worst Historical Loss Day"
            value={
              <span className="metric-value-loss">
                ${metrics.highestLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            }
            subtitle={
              <>
                <span className="metric-subtitle-primary">
                  {metrics.highestLossPercent}% of account ({metrics.numContracts || 1} contract{(metrics.numContracts || 1) > 1 ? 's' : ''})
                </span>
                {metrics.highestLossDate && (
                  <span className="metric-subtitle-date metric-subtitle-date-loss">Worst: {metrics.highestLossDate}</span>
                )}
              </>
            }
            icon={<IconTrendDown size={20} />}
            trend={metrics.apexMaeComparison?.exceedsMae ? 'negative' : 'neutral'}
            clickable={true}
            onClick={() => setSelectedMetric('highestLoss')}
          />

          {metrics.maxProfit !== undefined && metrics.maxProfit > 0 && (
            <MetricCard
              title="Maximum Profit"
              value={
                <span className="metric-value-profit">
                  ${metrics.maxProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={
                <>
                  <span className="metric-subtitle-primary">
                    {metrics.maxProfitPercent}% of account ({metrics.numContracts || 1} contract{(metrics.numContracts || 1) > 1 ? 's' : ''})
                  </span>
                  {metrics.maxProfitDate && (
                    <span className="metric-subtitle-date metric-subtitle-date-profit">Best: {metrics.maxProfitDate}</span>
                  )}
                </>
              }
              icon={<IconTrendUp size={20} />}
              trend="positive"
              clickable={true}
              onClick={() => setSelectedMetric('maxProfit')}
            />
          )}

          {metrics.avgLoss !== undefined && (
            <MetricCard
              title="Average Loss"
              value={
                <span className="metric-value-loss">
                  ${metrics.avgLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`${metrics.avgLossPercent}% of account (${metrics.numContracts || 1} contract${(metrics.numContracts || 1) > 1 ? 's' : ''})`}
              icon={<IconChart size={20} />}
              trend="negative"
              clickable={true}
              onClick={() => setSelectedMetric('averageLoss')}
            />
          )}

          {metrics.theoreticalStopLoss !== null && metrics.theoreticalStopLoss !== undefined && (
            <MetricCard
              title="SL-Based Theoretical Loss"
              value={
                <span className="metric-value-loss">
                  ${metrics.theoreticalStopLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`${metrics.theoreticalStopLossPercent || '0.00'}% of account (${metrics.strategySlLimitPoints || 0} pts SL)`}
              icon={<IconScale size={20} />}
              trend={metrics.apexMaeComparison?.exceedsMae ? 'negative' : 'moderate'}
              clickable={true}
              onClick={() => setSelectedMetric('slLoss')}
            />
          )}

          {metrics.avgProfit !== undefined && metrics.avgProfit > 0 && (
            <MetricCard
              title="Average Profit"
              value={
                <span className="metric-value-profit">
                  ${metrics.avgProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`${metrics.avgProfitPercent}% of account (${metrics.numContracts || 1} contract${(metrics.numContracts || 1) > 1 ? 's' : ''})`}
              icon={<IconTrendUp size={20} />}
              trend="positive"
              clickable={true}
              onClick={() => setSelectedMetric('averageProfit')}
            />
          )}

          {metrics.highestLossPerContract && (
            <MetricCard
              title="Per-Contract Loss"
              value={
                <span className="metric-value-loss">
                  ${metrics.highestLossPerContract.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              }
              subtitle={`Worst loss per contract`}
              icon={<IconScale size={20} />}
              trend="neutral"
            />
          )}

          <MetricCard
            title="Trading Days Analyzed"
            value={metrics.totalDays}
            subtitle={metrics.winningDays !== undefined 
              ? `${metrics.winningDays} winning, ${metrics.losingDays} losing days`
              : `${metrics.losingDays} losing days`}
            icon="📈"
            trend="neutral"
          />
        </div>
      )}

      {/* What to Fix Section - Show when there's a risk issue (% Rule mode) */}
      {riskMode === 'apexMae' && metrics.apexMaeComparison?.exceedsMae && (
        <div className="what-to-fix-section">
          <div className="what-to-fix-header">
            <IconLightbulb size={20} style={{ color: '#f59e0b' }} />
            <h3 className="what-to-fix-title">What to Fix</h3>
          </div>
          <div className="what-to-fix-content">
            <ul className="what-to-fix-list">
              {/* Suggestion 1: Reduce Contracts */}
              {formData?.contracts && Number(formData.contracts) > 1 && (
                <li className="what-to-fix-item">
                  <strong>Reduce Contracts:</strong> Consider reducing from <strong>{formData.contracts}</strong> contract{Number(formData.contracts) > 1 ? 's' : ''} to{' '}
                  {metrics.apexMaeComparison?.maxMaePerTrade ? (
                    <strong>{Math.max(1, Math.floor((metrics.apexMaeComparison.maxMaePerTrade / (metrics.highestLoss / Number(formData.contracts || 1))) * 0.9))}</strong>
                  ) : (
                    <strong>{Math.max(1, Math.floor(Number(formData.contracts) * 0.5))}</strong>
                  )}
                  {' '}contract{Math.max(1, Math.floor(Number(formData.contracts) * 0.5)) > 1 ? 's' : ''} to lower your risk exposure.
                </li>
              )}
              
              {/* Suggestion 2: Switch to MNQ */}
              {formData?.contractType === 'NQ' && (
                <li className="what-to-fix-item">
                  <strong>Switch to Micro Contracts (MNQ):</strong> MNQ contracts are 1/10th the size of NQ contracts, which means{' '}
                  {formData?.contracts ? `your current ${formData.contracts} NQ contract${Number(formData.contracts) > 1 ? 's' : ''}` : 'your NQ position'}{' '}
                  would be equivalent to <strong>{Number(formData?.contracts || 1) * 10}</strong> MNQ contracts. This gives you{' '}
                  <strong>10x more granular control</strong> over your position size and can help you stay within risk limits.
                </li>
              )}
              
              {/* Suggestion 3: Consider Alternative Strategies (Last Resort) */}
              {formData?.contractType === 'MNQ' && Number(formData?.contracts || 1) === 1 ? (
                <li className="what-to-fix-item">
                  <strong>Consider Alternative Strategies:</strong> Since you're already trading <strong>1 MNQ contract</strong> (the smallest position size), comparing a different strategy may be worth considering.
                  Review other available strategies using the same historical metrics and choose the one that best fits your own risk parameters and independent judgment.
                </li>
              ) : (
                <li className="what-to-fix-item">
                  <strong>Consider Alternative Strategies (Last Resort):</strong> If reducing contracts or switching to MNQ isn't sufficient, this strategy may not align with your risk tolerance or account size.
                  Try analyzing other strategies from the dropdown to find one with lower historical drawdowns, and use your own independent judgment when comparing available options.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Payout Planner temporarily hidden - may bring back later */}
      {false && riskMode === 'apexMae' && metrics.windfallRule && (
        <PayoutPlanner metrics={metrics} />
      )}

      {false && riskMode === 'apexMae' && metrics.windfallRule && (
        <div className="apex-mae-simplified-box" style={{ 
          borderColor: metrics.windfallRule.violatesWindfall === true ? '#ef4444' : metrics.windfallRule.violatesWindfall === false ? '#10b981' : '#f59e0b',
          backgroundColor: metrics.windfallRule.violatesWindfall === true ? 'rgba(239, 68, 68, 0.1)' : metrics.windfallRule.violatesWindfall === false ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)'
        }}>
          <div className="apex-mae-simplified-header">
            <div className="apex-mae-status-badge" style={{ 
              backgroundColor: metrics.windfallRule.violatesWindfall === true ? '#ef4444' : metrics.windfallRule.violatesWindfall === false ? '#10b981' : '#f59e0b',
              color: '#ffffff'
            }}>
              {metrics.windfallRule.violatesWindfall === true ? (
                <>
                  <IconAlert size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  VIOLATES RULE
                </>
              ) : metrics.windfallRule.violatesWindfall === false ? (
                <>
                  <IconCheck size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  WITHIN RULE
                </>
              ) : (
                <>
                  <IconInfo size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                  INFO
                </>
              )}
            </div>
            <h3 className="apex-mae-simplified-title">{activeConsistencyPercent}% Consistency Rule (Windfall)</h3>
          </div>
          
          <div className="apex-mae-simplified-content">
            {metrics.windfallRule.maxProfitTodayAllowed !== null && metrics.windfallRule.maxProfitTodayAllowed !== undefined ? (
              <div className="apex-mae-key-metric">
                <div className="apex-mae-metric-label">Maximum Profit You Can Make Today</div>
                <div className="apex-mae-metric-value" style={{ color: metrics.windfallRule.violatesWindfall ? '#f59e0b' : '#10b981' }}>
                  ${parseFloat(metrics.windfallRule.maxProfitTodayAllowed).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="apex-mae-metric-subtitle">
                  Without violating windfall rule (if today becomes highest day)
                </div>
              </div>
            ) : (
              <div className="apex-mae-key-metric">
                <div className="apex-mae-metric-label">Maximum Profit You Can Make Today</div>
                <div className="apex-mae-metric-value" style={{ color: '#f59e0b' }}>
                  N/A
                </div>
                <div className="apex-mae-metric-subtitle">
                  Enter profit balance to calculate
                </div>
              </div>
            )}

            <div className="apex-mae-key-metric">
              <div className="apex-mae-metric-label">Highest Historical Profit Day</div>
              <div className="apex-mae-metric-value" style={{ color: metrics.windfallRule.violatesWindfall ? '#ef4444' : '#10b981' }}>
                ${metrics.windfallRule.maxProfitDay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="apex-mae-metric-subtitle">
                Maximum single-day profit from historical data
              </div>
            </div>

            <div className="apex-mae-comparison">
              <div className="apex-mae-comparison-item">
                <span className="comparison-label">Your Highest Profit Day:</span>
                <span className={`comparison-value ${metrics.windfallRule.violatesWindfall ? 'exceeds' : 'safe'}`}>
                  ${metrics.windfallRule.maxProfitDay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              
              {metrics.windfallRule.profitBalanceForWindfall && (
                <div className="apex-mae-comparison-item">
                  <span className="comparison-label">Current Profit Balance:</span>
                  <span className="comparison-value">
                    ${parseFloat(metrics.windfallRule.profitBalanceForWindfall).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {metrics.windfallRule.maxProfitPercentOfBalance !== null && (
                <div className="apex-mae-comparison-item">
                  <span className="comparison-label">Highest Day as % of Profit Balance:</span>
                  <span className={`comparison-value ${metrics.windfallRule.violatesWindfall ? 'exceeds' : 'safe'}`}>
                    {metrics.windfallRule.maxProfitPercentOfBalance}%
                  </span>
                </div>
              )}
              
              {metrics.windfallRule.additionalProfitNeeded && parseFloat(metrics.windfallRule.additionalProfitNeeded) > 0 && (
                <div className="apex-mae-comparison-item">
                  <span className="comparison-label">Additional Profit Needed for Payout:</span>
                  <span className="comparison-value exceeds">
                    ${parseFloat(metrics.windfallRule.additionalProfitNeeded).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              
              {metrics.windfallRule.maxProfitTodayMessage && (
                <div className="apex-mae-info" style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'rgba(102, 126, 234, 0.15)',
                  borderLeft: '4px solid #667eea',
                  borderRadius: '8px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6'
                }}>
                  <strong><IconInfo size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> Today's Limit:</strong> {metrics.windfallRule.maxProfitTodayMessage}
                </div>
              )}
              
              {metrics.windfallRule.violatesWindfall === true ? (
                <div className="apex-mae-warning">
                  <strong><IconAlert size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> WARNING:</strong> {metrics.windfallRule.windfallMessage}
                </div>
              ) : metrics.windfallRule.violatesWindfall === false ? (
                <div className="apex-mae-safe">
                  <strong><IconCheck size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> SAFE:</strong> {metrics.windfallRule.windfallMessage}
                </div>
              ) : (
                <div className="apex-mae-info" style={{
                  padding: '1rem',
                  background: 'rgba(245, 158, 11, 0.15)',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '8px',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6'
                }}>
                  <strong><IconInfo size={16} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} /> INFO:</strong> {metrics.windfallRule.windfallMessage}
                </div>
              )}

              <div className="windfall-disclaimer" style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(107, 114, 128, 0.1)',
                borderLeft: '3px solid #6b7280',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.5'
              }}>
                <strong>📌 Important:</strong> The Windfall Rule applies to profit accumulated <strong>since your last approved payout</strong> (or since you started trading if no payouts yet). 
                {metrics.windfallRule.usesProfitSincePayout ? (
                  <> This calculation uses your "Profit Since Last Payout" input for accuracy.</>
                ) : (
                  <> This calculation uses your start-of-day profit balance. For more accuracy after payouts, enter your "Profit Since Last Payout" in the form above.</>
                )}
                {' '}Remember: The rule resets after each payout and applies until your 6th payout or account transfer.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="risk-message-box">
        <p className="risk-message-text">{metrics.riskMessage}</p>
      </div>

      <CalculationModal
        isOpen={selectedMetric !== null}
        onClose={() => setSelectedMetric(null)}
        metricType={selectedMetric}
        metrics={metrics}
        formData={formData}
      />

      {showMultiTpModal && (
        <div className="multi-tp-modal-overlay" onClick={() => setShowMultiTpModal(false)}>
          <div className="multi-tp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="multi-tp-modal-header">
              <h4>Multi-TP Modeling Note</h4>
              <button type="button" className="multi-tp-modal-close" onClick={() => setShowMultiTpModal(false)}>
                Close
              </button>
            </div>
            <p className="multi-tp-modal-text">{metrics.multiTpModelingNote}</p>
          </div>
        </div>
      )}

      {showRiskBasisModal && (
        <div className="multi-tp-modal-overlay" onClick={() => setShowRiskBasisModal(false)}>
          <div className="multi-tp-modal risk-basis-modal" onClick={(e) => e.stopPropagation()}>
            <div className="multi-tp-modal-header">
              <h4>How RiskLo Chooses Risk Inputs</h4>
              <button type="button" className="multi-tp-modal-close" onClick={() => setShowRiskBasisModal(false)}>
                Close
              </button>
            </div>
            <p className="multi-tp-modal-text">{metrics.riskCalculationNote}</p>
            {metrics.riskCalculationMethod === 'max_historical_vs_sl' && (
              <>
                <p className="multi-tp-modal-text" style={{ marginTop: '0.55rem' }}>
                  RiskLo compares both values for transparency. In live conditions, realized outcomes can exceed nominal stop settings due to slippage, execution differences, or partial fills, so historical loss may be higher than SL-based estimates.
                </p>
                <p className="multi-tp-modal-text" style={{ marginTop: '0.55rem', opacity: 0.9 }}>
                  Historical loss: ${Number(metrics.historicalHighestLoss || 0).toFixed(2)} | SL-based estimate: ${Number(metrics.theoreticalStopLoss || 0).toFixed(2)} | Risk basis used: ${Number(metrics.riskBasisLoss || metrics.highestLoss || 0).toFixed(2)}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;

